import { Injectable } from '@angular/core';
import { Observable, first, map } from 'rxjs';
import { BackendFeatureService } from '../backend-feature.service';

export const EDI_VIEWER_FEATURE_ID = 'gung-feature-edi.document-viewer';
export const EDI_VIEWER_PROTOCOL_VERSION = '2.3.0';

export type EdiViewerUpdateTier = 'frontend' | 'backend';

export interface EdiViewerCompatibility {
  compatible: boolean;
  frontendVersion: string;
  backendVersion?: string;
  updateTier?: EdiViewerUpdateTier;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class EdiViewerCompatibilityService {
  constructor(private backendFeatureService: BackendFeatureService) {}

  getCompatibility(): Observable<EdiViewerCompatibility> {
    return this.backendFeatureService.getAvailableFeatures().pipe(
      first(),
      map(features => {
        const backendVersion = features[EDI_VIEWER_FEATURE_ID]?.version;
        const frontendMajor = this.major(EDI_VIEWER_PROTOCOL_VERSION);
        const backendMajor = this.major(backendVersion);

        if (backendMajor === undefined) {
          return {
            compatible: false,
            frontendVersion: EDI_VIEWER_PROTOCOL_VERSION,
            backendVersion,
            updateTier: 'backend',
            message: `EDI viewer version mismatch. Backend does not advertise a valid viewer protocol and frontend is ${EDI_VIEWER_PROTOCOL_VERSION}. Update the backend tier.`
          };
        }
        if (backendMajor === frontendMajor) {
          return {
            compatible: true,
            frontendVersion: EDI_VIEWER_PROTOCOL_VERSION,
            backendVersion
          };
        }

        const updateTier: EdiViewerUpdateTier = backendMajor < frontendMajor ? 'backend' : 'frontend';
        return {
          compatible: false,
          frontendVersion: EDI_VIEWER_PROTOCOL_VERSION,
          backendVersion,
          updateTier,
          message: `EDI viewer version mismatch. Backend is ${backendVersion} and frontend is ${EDI_VIEWER_PROTOCOL_VERSION}. Update the ${updateTier} tier.`
        };
      })
    );
  }

  private major(version?: string): number | undefined {
    const match = version?.trim().match(/^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/);
    return match ? Number(match[1]) : undefined;
  }
}
